/**
 * WebCodecs Subtitle Burner
 * 
 * Ultra-fast subtitle burning using WebCodecs (GPU accelerated)
 * For Constance Workflow - burns subtitles directly into video at max speed
 * Audio is copied from source using FFmpeg (no re-encoding)
 */

import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { FFmpegService } from './ffmpeg-service';
import { SubtitleStyle } from './types';

export interface BurnProgress {
    phase: 'decoding' | 'encoding' | 'muxing' | 'complete' | 'error';
    progress: number; // 0-100
    message: string;
    fps?: number; // Current encoding FPS
}

export type BurnProgressCallback = (progress: BurnProgress) => void;

interface ParsedSubtitle {
    startTime: number;
    endTime: number;
    text: string;
}

const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
    fontFamily: 'Futura, sans-serif',
    fontSize: 30,
    color: '#FFFFFF',
    shadowType: 'shadow',
    outlineWidth: 1.5,
    positionY: 10,
    padding: 5,
};

function getSubtitleStyle(style?: SubtitleStyle): SubtitleStyle {
    return style || DEFAULT_SUBTITLE_STYLE;
}

function wrapSubtitleLines(
    ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string[] {
    const paragraphs = text.split('\n');
    const wrapped: string[] = [];

    for (const paragraph of paragraphs) {
        const words = paragraph.trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) continue;

        let currentLine = words[0];
        for (let i = 1; i < words.length; i++) {
            const testLine = `${currentLine} ${words[i]}`;
            if (ctx.measureText(testLine).width > maxWidth && currentLine !== '') {
                wrapped.push(currentLine);
                currentLine = words[i];
            } else {
                currentLine = testLine;
            }
        }
        wrapped.push(currentLine);
    }

    return wrapped;
}

/**
 * Parse VTT content into subtitle objects
 */
function parseVTT(vttContent: string): ParsedSubtitle[] {
    const subtitles: ParsedSubtitle[] = [];
    const lines = vttContent.split('\n');
    let i = 0;

    // Skip WEBVTT header
    while (i < lines.length && !lines[i].includes('-->')) {
        i++;
    }

    while (i < lines.length) {
        const line = lines[i].trim();

        if (line.includes('-->')) {
            const timeParts = line.split('-->');
            const startTime = parseVTTTime(timeParts[0].trim());
            const endTime = parseVTTTime(timeParts[1].trim().split(' ')[0]); // Remove position info

            // Collect text lines
            i++;
            const textLines: string[] = [];
            while (i < lines.length && lines[i].trim() !== '' && !lines[i].includes('-->')) {
                textLines.push(lines[i].trim());
                i++;
            }

            if (textLines.length > 0) {
                subtitles.push({
                    startTime,
                    endTime,
                    text: textLines.join('\n')
                });
            }
        } else {
            i++;
        }
    }

    return subtitles;
}

function parseVTTTime(timeStr: string): number {
    const parts = timeStr.split(':');
    if (parts.length === 3) {
        const [hours, minutes, secondsMs] = parts;
        const [seconds, ms] = secondsMs.split('.');
        return parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds) + parseInt(ms || '0') / 1000;
    } else if (parts.length === 2) {
        const [minutes, secondsMs] = parts;
        const [seconds, ms] = secondsMs.split('.');
        return parseInt(minutes) * 60 + parseInt(seconds) + parseInt(ms || '0') / 1000;
    }
    return 0;
}

/**
 * Get active subtitle at a given time
 */
function getActiveSubtitle(subtitles: ParsedSubtitle[], time: number): ParsedSubtitle | null {
    return subtitles.find(sub => time >= sub.startTime && time <= sub.endTime) || null;
}

/**
 * Draw subtitle on canvas
 */
function drawSubtitle(
    ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
    subtitle: ParsedSubtitle,
    width: number,
    height: number,
    subtitleStyle?: SubtitleStyle
) {
    const style = getSubtitleStyle(subtitleStyle);
    const fontSize = Math.max(1, Math.round(style.fontSize));
    const padding = Math.max(0, Math.round(width * ((style.padding ?? DEFAULT_SUBTITLE_STYLE.padding) / 100)));
    const bottomMargin = height * ((style.positionY ?? DEFAULT_SUBTITLE_STYLE.positionY) / 100);

    ctx.save();
    ctx.font = `bold ${fontSize}px ${style.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    const maxWidth = Math.max(200, width - padding * 2);
    const lines = wrapSubtitleLines(ctx, subtitle.text, maxWidth);
    if (lines.length === 0) {
        ctx.restore();
        return;
    }

    const lineHeight = fontSize * 1.2;
    const textBottomY = height - bottomMargin;
    const firstLineY = textBottomY - ((lines.length - 1) * lineHeight);

    if (style.shadowType === 'shadow') {
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = Math.max(2, Math.round(fontSize * 0.15));
        ctx.shadowOffsetX = Math.max(1, Math.round(fontSize * 0.08));
        ctx.shadowOffsetY = Math.max(1, Math.round(fontSize * 0.08));
    } else {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
    }

    ctx.fillStyle = style.color;
    if (style.shadowType === 'outline') {
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = style.outlineWidth ?? Math.max(1.5, fontSize * 0.05);
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
    }

    lines.forEach((line, idx) => {
        const y = firstLineY + idx * lineHeight;
        const x = width / 2;
        if (style.shadowType === 'outline') {
            ctx.strokeText(line, x, y);
        }
        ctx.fillText(line, x, y);
    });

    ctx.restore();
}

interface CodecConfig {
    codec: string;
    muxerCodec: 'avc' | 'vp9';
}

/**
 * Find the best supported video codec
 */
async function findBestCodec(width: number, height: number, fps: number): Promise<CodecConfig> {
    // Try H.264 first (best compatibility with MP4)
    const h264Configs = [
        'avc1.640028', // High Profile Level 4.0
        'avc1.4D0028', // Main Profile Level 4.0
        'avc1.42E01E', // Baseline Profile Level 3.0
    ];

    for (const codec of h264Configs) {
        const config: VideoEncoderConfig = {
            codec,
            width,
            height,
            bitrate: 8_000_000,
            framerate: fps,
        };
        try {
            const support = await VideoEncoder.isConfigSupported(config);
            if (support.supported) {
                console.log(`[WebCodecs] Using codec: ${codec}`);
                return { codec, muxerCodec: 'avc' };
            }
        } catch (e) {
            // Continue to next codec
        }
    }

    // Fallback to VP9
    const vp9Config: VideoEncoderConfig = {
        codec: 'vp09.00.10.08',
        width,
        height,
        bitrate: 8_000_000,
        framerate: fps,
    };
    try {
        const support = await VideoEncoder.isConfigSupported(vp9Config);
        if (support.supported) {
            console.log('[WebCodecs] Using codec: VP9');
            return { codec: 'vp09.00.10.08', muxerCodec: 'vp9' };
        }
    } catch (e) {
        // No VP9 available
    }

    throw new Error('No supported video codec found (H.264/VP9). Try using Chrome or Edge.');
}

/**
 * Burn subtitles into video using WebCodecs + FFmpeg audio mux
 * Maximum speed - uses hardware acceleration for video
 * Audio is copied from source (no re-encoding)
 */
export async function burnSubtitlesWebCodecs(
    videoSource: Blob | string,
    vttContent: string,
    onProgress: BurnProgressCallback,
    subtitleStyle?: SubtitleStyle
): Promise<Blob> {
    console.log('[WebCodecs Burner] Starting...');

    // Parse subtitles
    const subtitles = parseVTT(vttContent);
    console.log(`[WebCodecs Burner] Parsed ${subtitles.length} subtitles`);

    onProgress({ phase: 'decoding', progress: 0, message: 'Chargement vidéo...' });

    // Create video element to get metadata and decode frames
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous'; // Required for VideoFrame from cross-origin sources

    // Load video
    const videoUrl = videoSource instanceof Blob
        ? URL.createObjectURL(videoSource)
        : videoSource;

    video.src = videoUrl;

    await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Failed to load video'));
    });

    const width = video.videoWidth;
    const height = video.videoHeight;
    const duration = video.duration;
    const fps = 30; // Target FPS
    const totalFrames = Math.ceil(duration * fps);

    console.log(`[WebCodecs Burner] Video: ${width}x${height}, ${duration.toFixed(2)}s, ${totalFrames} frames`);

    onProgress({ phase: 'decoding', progress: 5, message: `Vidéo: ${width}x${height}` });

    // Create canvas for compositing
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d', { alpha: false })!;

    // Find best supported codec
    const codecConfig = await findBestCodec(width, height, fps);

    // Setup MP4 muxer (video only)
    const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: {
            codec: codecConfig.muxerCodec,
            width,
            height,
        },
        fastStart: 'in-memory',
    });

    // Initialize encoder
    const encoderConfig: VideoEncoderConfig = {
        codec: codecConfig.codec,
        width,
        height,
        bitrate: 8_000_000, // 8 Mbps
        framerate: fps,
        latencyMode: 'quality',
    };

    const videoEncoder = new VideoEncoder({
        output: (chunk, metadata) => {
            muxer.addVideoChunk(chunk, metadata);
        },
        error: (e) => {
            console.error('[WebCodecs Burner] Encoder error:', e);
        }
    });

    videoEncoder.configure(encoderConfig);

    onProgress({ phase: 'encoding', progress: 10, message: 'Encodage vidéo GPU...' });

    // Process frames sequentially (ensures monotonic timestamps)
    const startTime = performance.now();
    let framesEncoded = 0;

    for (let frame = 0; frame < totalFrames; frame++) {
        const currentTime = frame / fps;

        // Seek video to current time
        video.currentTime = currentTime;
        await new Promise<void>(resolve => {
            video.onseeked = () => resolve();
        });

        // Draw video frame
        ctx.drawImage(video, 0, 0, width, height);

        // Find and draw active subtitle
        const activeSub = getActiveSubtitle(subtitles, currentTime);
        if (activeSub) {
            drawSubtitle(ctx, activeSub, width, height, subtitleStyle);
        }

        // Create video frame and encode
        const videoFrame = new VideoFrame(canvas, {
            timestamp: frame * (1_000_000 / fps), // microseconds
            duration: 1_000_000 / fps,
        });

        const keyFrame = frame % (fps * 2) === 0; // Keyframe every 2 seconds
        videoEncoder.encode(videoFrame, { keyFrame });
        videoFrame.close();

        framesEncoded++;

        // Progress update every 30 frames
        if (frame % 30 === 0) {
            const elapsed = (performance.now() - startTime) / 1000;
            const currentFps = framesEncoded / elapsed;
            const progress = 10 + Math.floor((frame / totalFrames) * 70);

            onProgress({
                phase: 'encoding',
                progress,
                message: `Frame ${frame}/${totalFrames} (${currentFps.toFixed(1)} fps)`,
                fps: currentFps
            });
        }
    }

    // Flush encoder
    await videoEncoder.flush();
    videoEncoder.close();

    onProgress({ phase: 'muxing', progress: 85, message: 'Vidéo encodée, ajout audio...' });

    // Finalize video-only muxer
    muxer.finalize();
    const { buffer: videoOnlyBuffer } = muxer.target;
    const videoOnlyBlob = new Blob([videoOnlyBuffer], { type: 'video/mp4' });

    console.log(`[WebCodecs Burner] Video encoded: ${(videoOnlyBlob.size / 1024 / 1024).toFixed(2)} MB`);

    // Now use FFmpeg to copy audio from source and mux with new video
    onProgress({ phase: 'muxing', progress: 90, message: 'Fusion audio (FFmpeg)...' });

    try {
        const ffmpeg = await FFmpegService.getInstance();

        // Get source video as blob
        let sourceBlob: Blob;
        if (videoSource instanceof Blob) {
            sourceBlob = videoSource;
        } else {
            const resp = await fetch(videoSource);
            sourceBlob = await resp.blob();
        }

        // Write files to FFmpeg
        const sourceData = new Uint8Array(await sourceBlob.arrayBuffer());
        const videoOnlyData = new Uint8Array(await videoOnlyBlob.arrayBuffer());

        await ffmpeg.writeFile('source.mp4', sourceData);
        await ffmpeg.writeFile('video_only.mp4', videoOnlyData);

        // Mux: copy video from webcodecs output, copy audio from source
        await ffmpeg.exec([
            '-i', 'video_only.mp4',  // WebCodecs video
            '-i', 'source.mp4',       // Original with audio
            '-c:v', 'copy',           // Copy video (no re-encode)
            '-c:a', 'aac',            // Re-encode audio to AAC for compatibility
            '-b:a', '192k',           // Audio bitrate
            '-map', '0:v:0',          // Video from first input
            '-map', '1:a:0?',         // Audio from second input (optional)
            '-shortest',              // Match shortest stream
            '-y',
            'output.mp4'
        ]);

        // Read result
        const outputData = await ffmpeg.readFile('output.mp4') as Uint8Array;
        const result = new Blob([outputData.slice().buffer], { type: 'video/mp4' });

        // Cleanup FFmpeg files
        await ffmpeg.deleteFile('source.mp4');
        await ffmpeg.deleteFile('video_only.mp4');
        await ffmpeg.deleteFile('output.mp4');

        // Cleanup video URL
        if (videoSource instanceof Blob) {
            URL.revokeObjectURL(videoUrl);
        }

        const totalTime = (performance.now() - startTime) / 1000;
        const avgFps = totalFrames / totalTime;

        console.log(`[WebCodecs Burner] Complete with audio: ${(result.size / 1024 / 1024).toFixed(2)} MB in ${totalTime.toFixed(1)}s`);

        onProgress({
            phase: 'complete',
            progress: 100,
            message: `Terminé! ${avgFps.toFixed(1)} fps moyen`,
            fps: avgFps
        });

        return result;

    } catch (ffmpegError) {
        console.warn('[WebCodecs Burner] FFmpeg audio mux failed, returning video-only:', ffmpegError);

        // Fallback: return video without audio
        if (videoSource instanceof Blob) {
            URL.revokeObjectURL(videoUrl);
        }

        const totalTime = (performance.now() - startTime) / 1000;
        const avgFps = totalFrames / totalTime;

        onProgress({
            phase: 'complete',
            progress: 100,
            message: `Terminé (sans audio)! ${avgFps.toFixed(1)} fps`,
            fps: avgFps
        });

        return videoOnlyBlob;
    }
}

/**
 * Check if WebCodecs is available
 */
export function isWebCodecsSupported(): boolean {
    return 'VideoEncoder' in window && 'VideoDecoder' in window && 'VideoFrame' in window;
}

/**
 * Encode a single part of the video (used for parallel encoding)
 */
async function encodeVideoPart(
    videoUrl: string,
    subtitles: ParsedSubtitle[],
    startFrame: number,
    endFrame: number,
    fps: number,
    width: number,
    height: number,
    partIndex: number,
    codecConfig: CodecConfig,
    subtitleStyle: SubtitleStyle,
    onFrameEncoded: (partIndex: number, framesEncoded: number) => void
): Promise<Blob> {
    // Create video element for this part
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.src = videoUrl;

    await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error(`Failed to load video for part ${partIndex}`));
    });

    // Create canvas for this part
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d', { alpha: false })!;

    // Create muxer for this part
    const muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: {
            codec: codecConfig.muxerCodec,
            width,
            height,
        },
        fastStart: 'in-memory',
    });

    // Create encoder for this part
    const encoderConfig: VideoEncoderConfig = {
        codec: codecConfig.codec,
        width,
        height,
        bitrate: 8_000_000,
        framerate: fps,
        latencyMode: 'quality',
    };

    const videoEncoder = new VideoEncoder({
        output: (chunk, metadata) => {
            muxer.addVideoChunk(chunk, metadata);
        },
        error: (e) => {
            console.error(`[Part ${partIndex}] Encoder error:`, e);
        }
    });

    videoEncoder.configure(encoderConfig);

    // Encode frames for this part
    let framesEncoded = 0;
    for (let frame = startFrame; frame < endFrame; frame++) {
        const currentTime = frame / fps;

        video.currentTime = currentTime;
        await new Promise<void>(resolve => {
            video.onseeked = () => resolve();
        });

        ctx.drawImage(video, 0, 0, width, height);

        const activeSub = getActiveSubtitle(subtitles, currentTime);
        if (activeSub) {
            drawSubtitle(ctx, activeSub, width, height, subtitleStyle);
        }

        // Use relative timestamp for this part (starting from 0)
        const relativeFrame = frame - startFrame;
        const videoFrame = new VideoFrame(canvas, {
            timestamp: relativeFrame * (1_000_000 / fps),
            duration: 1_000_000 / fps,
        });

        const keyFrame = relativeFrame % (fps * 2) === 0;
        videoEncoder.encode(videoFrame, { keyFrame });
        videoFrame.close();

        framesEncoded++;
        onFrameEncoded(partIndex, framesEncoded);
    }

    await videoEncoder.flush();
    videoEncoder.close();

    muxer.finalize();
    const { buffer } = muxer.target;

    return new Blob([buffer], { type: 'video/mp4' });
}

/**
 * Burn subtitles using SEQUENTIAL chunked encoding (memory-safe for long videos)
 * Each chunk is encoded → written to FFmpeg FS → freed before next chunk starts.
 * This keeps peak memory usage proportional to ONE chunk, not the entire video.
 */
export async function burnSubtitlesSequential(
    videoSource: Blob | string,
    vttContent: string,
    numParts: number,
    onProgress: BurnProgressCallback,
    subtitleStyle?: SubtitleStyle
): Promise<Blob> {
    console.log(`[WebCodecs Sequential] Starting with ${numParts} parts...`);

    const subtitles = parseVTT(vttContent);
    console.log(`[WebCodecs Sequential] Parsed ${subtitles.length} subtitles`);
    const resolvedSubtitleStyle = getSubtitleStyle(subtitleStyle);

    onProgress({ phase: 'decoding', progress: 0, message: `Préparation (${numParts} parties)...` });

    // Create video element to get metadata
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';

    const videoUrl = videoSource instanceof Blob
        ? URL.createObjectURL(videoSource)
        : videoSource;

    video.src = videoUrl;

    await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject(new Error('Failed to load video'));
    });

    const width = video.videoWidth;
    const height = video.videoHeight;
    const duration = video.duration;
    const fps = 30;
    const totalFrames = Math.ceil(duration * fps);

    console.log(`[WebCodecs Sequential] Video: ${width}x${height}, ${totalFrames} frames, ${numParts} parts`);

    // Calculate frame ranges for each part
    const framesPerPart = Math.ceil(totalFrames / numParts);
    const partRanges: { start: number; end: number }[] = [];

    for (let i = 0; i < numParts; i++) {
        const start = i * framesPerPart;
        const end = Math.min(start + framesPerPart, totalFrames);
        if (start < totalFrames) {
            partRanges.push({ start, end });
        }
    }

    const actualParts = partRanges.length;
    console.log(`[WebCodecs Sequential] ${actualParts} actual parts:`, partRanges);

    // Find best codec once for all parts
    const codecConfig = await findBestCodec(width, height, fps);

    // Get FFmpeg ready early
    const ffmpeg = await FFmpegService.getInstance();

    const startTime = performance.now();
    const partProgress: number[] = new Array(actualParts).fill(0);
    let partsCompleted = 0;

    const MAX_CONCURRENT = 3; // Run up to 3 chunks simultaneously

    const updateProgress = () => {
        const totalEncoded = partProgress.reduce((a, b) => a + b, 0);
        const elapsed = (performance.now() - startTime) / 1000;
        const currentFps = totalEncoded / elapsed;
        const progress = 5 + Math.floor((totalEncoded / totalFrames) * 70);
        const running = Math.min(MAX_CONCURRENT, actualParts - partsCompleted);
        onProgress({
            phase: 'encoding',
            progress,
            message: `⚡ ${partsCompleted}/${actualParts} ✅ | ${running}x parallel — ${totalEncoded}/${totalFrames} frames (${currentFps.toFixed(1)} fps)`,
            fps: currentFps
        });
    };

    // ═══════════════════════════════════════════════
    // PARALLEL POOL: encode up to MAX_CONCURRENT chunks at once
    // Each chunk is written to FFmpeg FS immediately on completion
    // ═══════════════════════════════════════════════
    const encodeAndStore = async (pi: number) => {
        const range = partRanges[pi];

        const partBlob = await encodeVideoPart(
            videoUrl,
            subtitles,
            range.start,
            range.end,
            fps,
            width,
            height,
            pi,
            codecConfig,
            resolvedSubtitleStyle,
            (partIndex, framesEncoded) => {
                partProgress[partIndex] = framesEncoded;
                if (framesEncoded % 15 === 0) {
                    updateProgress();
                }
            }
        );

        // Immediately write to FFmpeg FS and free memory
        const partData = new Uint8Array(await partBlob.arrayBuffer());
        await ffmpeg.writeFile(`part${pi}.mp4`, partData);
        partsCompleted++;

        console.log(`[WebCodecs Pool] Part ${pi + 1}/${actualParts} done (${(partBlob.size / 1024 / 1024).toFixed(1)} MB) — ${partsCompleted}/${actualParts} complete`);
        updateProgress();
    };

    // Concurrency pool — like Promise.all but max N at a time
    let nextPart = 0;
    const runPool = async () => {
        const workers: Promise<void>[] = [];
        for (let w = 0; w < MAX_CONCURRENT; w++) {
            workers.push((async () => {
                while (true) {
                    const pi = nextPart++;
                    if (pi >= actualParts) break;
                    await encodeAndStore(pi);
                }
            })());
        }
        await Promise.all(workers);
    };

    await runPool();

    console.log(`[WebCodecs Pool] All ${actualParts} parts encoded & stored in FFmpeg FS`);

    // ═══════════════════════════════════════════════
    // CONCATENATE parts with FFmpeg
    // ═══════════════════════════════════════════════
    onProgress({ phase: 'muxing', progress: 78, message: 'Concaténation des parties...' });

    const concatList = partRanges.map((_, i) => `file 'part${i}.mp4'`).join('\n');
    await ffmpeg.writeFile('concat.txt', new TextEncoder().encode(concatList));

    await ffmpeg.exec([
        '-f', 'concat',
        '-safe', '0',
        '-i', 'concat.txt',
        '-c', 'copy',
        '-y',
        'video_concat.mp4'
    ]);

    // Cleanup part files immediately
    for (let i = 0; i < actualParts; i++) {
        await ffmpeg.deleteFile(`part${i}.mp4`).catch(() => { });
    }
    await ffmpeg.deleteFile('concat.txt').catch(() => { });

    // ═══════════════════════════════════════════════
    // ADD AUDIO from source
    // ═══════════════════════════════════════════════
    onProgress({ phase: 'muxing', progress: 88, message: 'Ajout audio...' });

    let sourceBlob: Blob;
    if (videoSource instanceof Blob) {
        sourceBlob = videoSource;
    } else {
        const resp = await fetch(videoSource);
        sourceBlob = await resp.blob();
    }

    const sourceData = new Uint8Array(await sourceBlob.arrayBuffer());
    await ffmpeg.writeFile('source.mp4', sourceData);
    // @ts-ignore - intentional deref
    sourceBlob = null!;

    await ffmpeg.exec([
        '-i', 'video_concat.mp4',
        '-i', 'source.mp4',
        '-c:v', 'copy',
        '-c:a', 'aac',
        '-b:a', '192k',
        '-map', '0:v:0',
        '-map', '1:a:0?',
        '-shortest',
        '-y',
        'output.mp4'
    ]);

    // Read result
    const outputData = await ffmpeg.readFile('output.mp4') as Uint8Array;
    const result = new Blob([outputData.slice().buffer], { type: 'video/mp4' });

    // Cleanup
    await ffmpeg.deleteFile('video_concat.mp4').catch(() => { });
    await ffmpeg.deleteFile('source.mp4').catch(() => { });
    await ffmpeg.deleteFile('output.mp4').catch(() => { });

    if (videoSource instanceof Blob) {
        URL.revokeObjectURL(videoUrl);
    }

    const totalTime = (performance.now() - startTime) / 1000;
    const avgFps = totalFrames / totalTime;

    console.log(`[WebCodecs Pool] Complete: ${totalFrames} frames in ${totalTime.toFixed(1)}s (${avgFps.toFixed(1)} fps, ${actualParts} parts, ${MAX_CONCURRENT}x concurrency)`);

    onProgress({
        phase: 'complete',
        progress: 100,
        message: `Terminé! ${avgFps.toFixed(1)} fps (${actualParts} parties, ${MAX_CONCURRENT}x)`,
        fps: avgFps
    });

    return result;
}

/**
 * Legacy parallel version (kept for short videos where memory isn't an issue)
 */
export async function burnSubtitlesParallel(
    videoSource: Blob | string,
    vttContent: string,
    numParts: number,
    onProgress: BurnProgressCallback,
    subtitleStyle?: SubtitleStyle
): Promise<Blob> {
    // For safety, just delegate to sequential version
    return burnSubtitlesSequential(videoSource, vttContent, numParts, onProgress, subtitleStyle);
}
